import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Drawer, Box, Typography, IconButton, Button, Divider } from '@mui/material';
import { updateQuantity, removeFromCart } from '../../store/slices/cartSlice';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';

// ─── Design tokens ─────────────────────────────────────────────────────────────
// Figma base: 1920px viewport → 672px drawer.
// Drawer width: xs=100vw, sm+=clamp(480px, 35vw, 672px)
//   35vw hits 672px at 1920px ✓ and floors to 480px below ~1371px.
// All sm+ values: clamp(floor, X vw, figma-value)  where X = value/1920×100
// All xs values:  clamp(floor, Y vw, sm-floor)      where Y = sm-floor/600×100
//   (so the value smoothly reaches the sm floor exactly at the 600px breakpoint)

const BLUE   = '#178ACB';
const FONT   = 'Manrope, sans-serif';
const POPPINS = 'Poppins, sans-serif';
const ICON   = (name) => `${process.env.PUBLIC_URL}/assets/images/icons/carts-icon/${name}`;

// ─── Responsive token map ──────────────────────────────────────────────────────
const R = {
  // ── Header ─────────────────────────────────────────────────────────────────
  // Figma: px=40, py=30, font=20
  headerPx:     { xs: 'clamp(16px, 4.67vw, 28px)',    sm: 'clamp(28px, 2.08vw, 40px)'  },
  headerPy:     { xs: 'clamp(12px, 3.33vw, 20px)',    sm: 'clamp(20px, 1.56vw, 30px)'  },
  headerFont:   { xs: 'clamp(12px, 2.5vw, 15px)',     sm: 'clamp(15px, 1.04vw, 20px)'  },

  // ── Items area ──────────────────────────────────────────────────────────────
  // Figma: px=40, pt=30, gap-between-items=32
  itemsPx:      { xs: 'clamp(16px, 4.67vw, 28px)',    sm: 'clamp(28px, 2.08vw, 40px)'  },
  itemsPt:      { xs: 'clamp(14px, 3.67vw, 22px)',    sm: 'clamp(22px, 1.56vw, 30px)'  },
  itemGap:      { xs: 'clamp(14px, 3.67vw, 22px)',    sm: 'clamp(22px, 1.67vw, 32px)'  },

  // ── Product image  (Figma: 83.72 × 93.44) ──────────────────────────────────
  imgW:         { xs: 'clamp(44px, 10vw, 60px)',       sm: 'clamp(60px, 4.36vw, 84px)'  },
  imgH:         { xs: 'clamp(50px, 11.33vw, 68px)',    sm: 'clamp(68px, 4.87vw, 94px)'  },
  imgGap:       { xs: 'clamp(10px, 2.67vw, 16px)',     sm: 'clamp(16px, 1.25vw, 24px)'  },

  // ── Text ───────────────────────────────────────────────────────────────────
  // Figma: name=16, price=24
  nameFont:     { xs: 'clamp(11px, 2.17vw, 13px)',    sm: 'clamp(13px, 0.83vw, 16px)'  },
  priceFont:    { xs: 'clamp(13px, 2.83vw, 17px)',    sm: 'clamp(17px, 1.25vw, 24px)'  },

  // ── Qty stepper  (Figma: 108.67 × 53) ──────────────────────────────────────
  qtyW:         { xs: 'clamp(66px, 13vw, 78px)',       sm: 'clamp(78px, 5.66vw, 109px)' },
  qtyH:         { xs: 'clamp(32px, 6.33vw, 38px)',     sm: 'clamp(38px, 2.76vw, 53px)'  },
  qtyBtnFont:   { xs: 'clamp(14px, 2.67vw, 16px)',    sm: 'clamp(16px, 1.15vw, 22px)'  },
  qtyNumFont:   { xs: 'clamp(12px, 2.33vw, 14px)',    sm: 'clamp(14px, 0.83vw, 16px)'  },
  delIcon:      { xs: 'clamp(16px, 3.33vw, 20px)',    sm: 'clamp(20px, 1.25vw, 24px)'  },

  // ── Footer ─────────────────────────────────────────────────────────────────
  // Figma: subtotal=24, btn-font=16, btn-h=44, btn-gap=24, footer-pb=40
  subtotalFont: { xs: 'clamp(14px, 2.83vw, 17px)',    sm: 'clamp(17px, 1.25vw, 24px)'  },
  btnFont:      { xs: 'clamp(12px, 2.33vw, 14px)',    sm: 'clamp(14px, 0.83vw, 16px)'  },
  btnH:         { xs: '40px',                          sm: 'clamp(40px, 2.29vw, 44px)'  },
  btnGap:       { xs: 'clamp(10px, 2.67vw, 16px)',    sm: 'clamp(16px, 1.25vw, 24px)'  },
  footerPt:     { xs: 'clamp(14px, 3.33vw, 20px)',    sm: 'clamp(20px, 1.46vw, 28px)'  },
  footerPb:     { xs: 'clamp(20px, 4.67vw, 28px)',    sm: 'clamp(28px, 2.08vw, 40px)'  },
};

const fmt = (value) =>
  '₹' + new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2 }).format(Number(value) || 0);

// ─── Qty stepper ───────────────────────────────────────────────────────────────
const QtyControl = ({ value, onMinus, onPlus }) => (
  <Box
    sx={{
      width: R.qtyW,
      height: R.qtyH,
      px: '8px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderRadius: '8px',
      border: '0.80px solid #E2E2E2',
      overflow: 'hidden',
      flexShrink: 0,
    }}
  >
    {[{ label: '-', fn: onMinus }, { label: null }, { label: '+', fn: onPlus }].map(({ label, fn }, i) =>
      label === null ? (
        <Typography
          key="count"
          sx={{
            color: '#585858',
            fontFamily: FONT,
            fontSize: R.qtyNumFont,
            fontWeight: 400,
            lineHeight: '26px',
            wordWrap: 'break-word',
            minWidth: '16px',
            textAlign: 'center',
            userSelect: 'none',
          }}
        >
          {value}
        </Typography>
      ) : (
        <Box
          key={label}
          component="button"
          onClick={fn}
          sx={{
            width: '24px',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            p: 0,
            color: '#585858',
            fontFamily: POPPINS,
            fontSize: R.qtyBtnFont,
            fontWeight: 300,
            textTransform: 'capitalize',
            lineHeight: '23.59px',
            wordWrap: 'break-word',
            flexShrink: 0,
            '&:hover': { color: BLUE },
          }}
        >
          {label}
        </Box>
      )
    )}
  </Box>
);

// ─── Single cart item ──────────────────────────────────────────────────────────
const CartItem = ({ item, onQtyChange, onRemove }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    {/* Left: image + name/price */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: R.imgGap, minWidth: 0, flex: 1 }}>
      <Box
        sx={{
          width: R.imgW,
          height: R.imgH,
          bgcolor: '#FFFFFF',
          borderRadius: '6px',
          flexShrink: 0,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {item.image && (
          <Box
            component="img"
            src={item.image}
            alt={item.name}
            sx={{ width: '100%', height: '100%', objectFit: 'contain', mixBlendMode: 'multiply' }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
        <Typography
          sx={{
            color: 'black',
            fontFamily: FONT,
            fontSize: R.nameFont,
            fontWeight: 500,
            wordWrap: 'break-word',
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {item.name}
        </Typography>
        <Typography sx={{ color: 'var(--color-grey-11, #1C1C1C)', fontFamily: FONT, fontSize: R.priceFont, fontWeight: 500, wordWrap: 'break-word' }}>
          {fmt(item.price)}
        </Typography>
      </Box>
    </Box>

    {/* Right: stepper + delete */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0, ml: '8px' }}>
      <QtyControl
        value={item.qty || 1}
        onMinus={() => onQtyChange(item, -1)}
        onPlus={() => onQtyChange(item, +1)}
      />
      <IconButton
        size="small"
        onClick={() => onRemove(item)}
        disableRipple
        aria-label="Remove item"
        sx={{ p: 0, '&:hover': { bgcolor: 'transparent', opacity: 0.7 } }}
      >
        <Box component="img" src={ICON('Frame.svg')} alt="" aria-hidden="true" sx={{ width: R.delIcon, height: R.delIcon }} />
      </IconButton>
    </Box>
  </Box>
);

// ─── Cart Drawer ───────────────────────────────────────────────────────────────
const CartDrawer = ({ open, onClose }) => {
  const { items, total, quantity } = useSelector((state) => state.cart);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const changeQty = (item, delta) => {
    const next = (item.qty || 1) + delta;
    if (next <= 0) {
      dispatch(removeFromCart({ productId: item.productId || item.id, variantId: item.variant_id }));
    } else {
      dispatch(updateQuantity({ productId: item.productId || item.id, quantity: next, variantId: item.variant_id }));
    }
  };

  const removeItem = (item) =>
    dispatch(removeFromCart({ productId: item.productId || item.id, variantId: item.variant_id }));

  const goTo = (path) => {
    onClose();
    if (path === '/checkout' && !isAuthenticated) {
      navigate('/login', { state: { from: '/checkout' } });
    } else {
      navigate(path);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          // xs: full screen | sm+: scales 480px→672px between 1371px–1920px viewport
          width: { xs: '100%', sm: 'clamp(480px, 35vw, 672px)' },
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          bgcolor: 'white',
        },
      }}
    >
      {/* ── Header ─────────────────────────────────────────────── */}
      <Box
        sx={{
          bgcolor: BLUE,
          px: R.headerPx,
          py: R.headerPy,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <Typography sx={{ color: 'white', fontFamily: FONT, fontSize: R.headerFont, fontWeight: 600 }}>
          My Cart ({quantity})
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          disableRipple
          aria-label="Close cart"
          sx={{ p: 0, '&:hover': { bgcolor: 'transparent', opacity: 0.75 } }}
        >
          <Box component="img" src={ICON('close-white.svg')} alt="" aria-hidden="true" sx={{ width: R.delIcon, height: R.delIcon }} />
        </IconButton>
      </Box>

      {/* ── Scrollable item list ────────────────────────────────── */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: R.itemsPx, pt: R.itemsPt }}>
        {items.length === 0 ? (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <Box
              component="img"
              src={ICON('shopping-cart.svg')}
              alt=""
              aria-hidden="true"
              sx={{ width: '40px', height: '40px', opacity: 0.3, mb: 2 }}
            />
            <Typography sx={{ fontFamily: FONT, fontSize: R.nameFont, color: '#6b7280', mb: 3 }}>
              Your cart is empty
            </Typography>
            <Button
              variant="contained"
              onClick={() => goTo('/shop')}
              disableElevation
              sx={{
                bgcolor: BLUE, fontFamily: FONT, fontSize: R.btnFont, fontWeight: 600,
                textTransform: 'none', borderRadius: '6px', height: R.btnH, px: '20px',
                '&:hover': { bgcolor: '#1270a8' },
              }}
            >
              Continue Shopping
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: R.itemGap, pb: R.itemsPt }}>
            {items.map((item, idx) => (
              <React.Fragment key={item.id || item.productId || idx}>
                {idx > 0 && <Divider sx={{ borderColor: '#E2E2E2' }} />}
                <CartItem
                  item={item}
                  onQtyChange={changeQty}
                  onRemove={removeItem}
                />
              </React.Fragment>
            ))}
          </Box>
        )}
      </Box>

      {/* ── Sticky footer ──────────────────────────────────────── */}
      {items.length > 0 && (
        <Box
          sx={{
            borderTop: '1px solid #E2E2E2',
            px: R.itemsPx,
            pt: R.footerPt,
            pb: R.footerPb,
            flexShrink: 0,
            bgcolor: 'white',
            display: 'flex',
            flexDirection: 'column',
            gap: R.footerPt,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ color: 'black', fontFamily: FONT, fontSize: R.subtotalFont, fontWeight: 400 }}>
              Subtotal
            </Typography>
            <Typography sx={{ color: '#1C1C1C', fontFamily: FONT, fontSize: R.subtotalFont, fontWeight: 400 }}>
              {fmt(total)}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: R.btnGap }}>
            <Button
              fullWidth onClick={() => goTo('/checkout')} disableElevation
              sx={{
                flex: '1 1 0', height: R.btnH, px: '16px', bgcolor: BLUE, color: 'white',
                borderRadius: '8px', textTransform: 'none', fontFamily: FONT,
                fontSize: R.btnFont, fontWeight: 600, gap: '6px',
                '&:hover': { bgcolor: '#1270a8' },
              }}
            >
              Checkout
              <Box component="img" src={ICON('right24px.svg')} alt="" aria-hidden="true" sx={{ width: R.delIcon, height: R.delIcon, flexShrink: 0 }} />
            </Button>
            <Button
              fullWidth onClick={() => goTo('/cart')} disableElevation
              sx={{
                flex: '1 1 0', height: R.btnH, px: '16px', bgcolor: 'transparent', color: BLUE,
                borderRadius: '8px', border: '1px solid', borderColor: BLUE,
                textTransform: 'none', fontFamily: FONT, fontSize: R.btnFont, fontWeight: 600, gap: '6px',
                '&:hover': { bgcolor: '#f0f7ff' },
              }}
            >
              View Cart
              <Box component="img" src={ICON('shopping-cart.svg')} alt="" aria-hidden="true" sx={{ width: R.delIcon, height: R.delIcon, flexShrink: 0 }} />
            </Button>
          </Box>
        </Box>
      )}
    </Drawer>
  );
};

export default CartDrawer;
